import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Minus, Mail, ArrowRight } from "lucide-react";
import Grain from "../components/Grain";
import { PublicLatenessPolicy } from '../components/LatenessPolicy';

const FAQS = [
  {
    cat: "Avant la séance",
    items: [
      {
        q: "Comment réserver une séance ?",
        a: "Rendez-vous sur la page Réservation. Tu choisis le type de séance, une date, un créneau horaire, tes coordonnées, et tu règles l'acompte en ligne via Stripe. Tout se fait en moins de 3 minutes. Confirmation + fichier calendrier (.ics) envoyés instantanément par email.",
      },
      {
        q: "Combien coûte l'acompte ?",
        a: "50 % pour les nouvelles réservations de séances photo : Portrait & Branding, Maternité & Bébé, Célébration et toutes leurs formules, y compris les mariages et baptêmes. 100 % pour le Shooting de Noël et la location studio, sans changement. Le montant exact est affiché pour chaque formule. Solde à régler le jour de la séance, par TWINT ou en espèces. Pour les mariages avec options sur mesure, un devis personnalisé est établi. Les réservations déjà créées conservent leurs montants d’acompte et de solde.",
      },
      {
        q: "Puis-je annuler ou reporter ?",
        a: "Au moins 48 h avant la séance : annulation gratuite et un report gratuit maximum. Moins de 48 h avant la séance : en cas d’annulation ou de report, l’acompte est conservé. Pour le Shooting de Noël, le montant intégral est conservé à moins de 48 h. Contacte le studio par e-mail à studioeuclides25@gmail.com ou sur Instagram @studio_euclides pour toute demande.",
      },
      {
        q: "Quelle tenue prévoir ?",
        a: "Je privilégie les couleurs neutres et terreuses, les matières naturelles (lin, soie, coton, laine), les coupes épurées. Évite les imprimés serrés, les logos et le fluo. Prévois 2 à 3 tenues pour varier les ambiances — tu as un vestiaire privé. Je t'envoie un Welcome Pack détaillé avec ta confirmation.",
      },
    ],
  },
  {
    cat: "Le jour J",
    items: [
      {
        q: "Combien de temps dure vraiment la séance ?",
        a: "La durée indiquée inclut la préparation, les changements de tenue et les prises de vue : Grossesse et Portrait & Branding 1 h, Nouveau-né 2 à 3 h, Shooting de Noël 30 min. La séance se termine à l’heure réservée.",
      },
      {
        q: "Puis-je venir accompagné(e) ?",
        a: "Oui, bien sûr. Pour une maternité, le/la conjoint·e et les aînés sont inclus sans supplément. Pour un portrait, un·e proche peut t'accompagner (c'est souvent rassurant). Pour un branding, viens avec tes accessoires professionnels.",
      },
      {
        q: "Comment accéder au studio ?",
        a: "Studio Euclides, Route de Crochy 20, 1024 Écublens. Parking gratuit dans la rue (zone bleue, disque obligatoire). Bus 701 TL — arrêt Écublens Croisée, 4 min à pied. Je t'envoie le code d'accès 2 h avant la séance par email automatique.",
      },
    ],
  },
  {
    cat: "Après la séance",
    items: [
      {
        q: "Quand recevrai-je mes photos ?",
        a: "La galerie des séances studio est livrée sous 2 à 3 jours. Chaque photo supplémentaire coûte CHF 15. Les célébrations conservent leurs délais spécifiques. Le lien de la galerie privée est envoyé par email, avec les photos retouchées en haute définition.",
      },
      {
        q: "Les photos sont-elles retouchées ?",
        a: "Oui, toutes les photos livrées sont retouchées professionnellement : exposition, couleurs, peau (naturelle, pas lissée au point de ne plus te reconnaître). Pour une retouche beauté avancée ou des modifications spécifiques, c'est en supplément sur devis.",
      },
      {
        q: "Puis-je commander des tirages ou un album ?",
        a: "Oui. Album photo A4 souvenir dès CHF 290 (40 pages, impression Fine Art). Tirages Fine Art A4/A3 sur demande. Je m'occupe de tout, livraison en 3 à 4 semaines. Idéal cadeau mariage, naissance, anniversaire.",
      },
    ],
  },
];

export default function FAQ() {
  const [open, setOpen] = React.useState({ 0: 0 }); // first question open by default

  const toggle = (catIdx, itemIdx) => {
    const key = `${catIdx}-${itemIdx}`;
    setOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isOpen = (catIdx, itemIdx) => {
    const key = `${catIdx}-${itemIdx}`;
    if (open[key] !== undefined) return open[key];
    // Default: first item of first category open
    return catIdx === 0 && itemIdx === 0;
  };

  return (
    <div className="section-dark min-h-screen relative" data-testid="faq-page">
      <Grain />

      <section className="relative pt-28 pb-14 px-6 md:px-12">
        <div className="max-w-[1100px] mx-auto">
          <div className="eyebrow mb-4">Questions fréquentes</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] mb-6" data-testid="faq-title">
            Tout ce que tu te<br/>demandes <span className="italic-accent">avant</span> de réserver.
          </h1>
          <p className="text-text-cream/60 text-lg font-light max-w-2xl leading-relaxed">
            Les réponses aux questions fréquentes. Si tu ne trouves pas la tienne, écris-moi — je te réponds personnellement sous 24 h.
          </p>
        </div>
      </section>

      <section className="pb-24 px-6 md:px-12">
        <div className="max-w-[1100px] mx-auto">
          {FAQS.map((cat, catIdx) => (
            <div key={cat.cat} className="mb-16 last:mb-0">
              <div className="eyebrow text-accent-light mb-6">{cat.cat}</div>
              <div className="space-y-3">
                {cat.items.map((item, itemIdx) => {
                  const opened = isOpen(catIdx, itemIdx);
                  return (
                    <div
                      key={item.q}
                      className={`border rounded transition-all duration-300 overflow-hidden ${
                        opened ? "border-accent-deep/40 bg-accent-deep/5" : "border-white/10 bg-white/[0.02] hover:border-accent-deep/20"
                      }`}
                      data-testid={`faq-item-${catIdx}-${itemIdx}`}
                    >
                      <button
                        id={`faq-question-${catIdx}-${itemIdx}`}
                        aria-expanded={opened}
                        aria-controls={`faq-answer-${catIdx}-${itemIdx}`}
                        onClick={() => toggle(catIdx, itemIdx)}
                        className="w-full flex items-start justify-between gap-6 text-left p-5 md:p-6"
                        data-testid={`faq-toggle-${catIdx}-${itemIdx}`}
                      >
                        <span className="font-serif text-lg md:text-xl text-text-cream flex-1">{item.q}</span>
                        <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border transition-all ${
                          opened ? "bg-accent-deep border-accent-deep text-white rotate-45" : "border-white/15 text-text-cream/60"
                        }`}>
                          <Plus size={16} />
                        </span>
                      </button>
                      <motion.div
                        id={`faq-answer-${catIdx}-${itemIdx}`}
                        role="region"
                        aria-labelledby={`faq-question-${catIdx}-${itemIdx}`}
                        aria-hidden={!opened}
                        initial={false}
                        animate={{ height: opened ? "auto" : 0, opacity: opened ? 1 : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                          <p className="text-text-cream/70 font-light leading-relaxed max-w-3xl">{item.a}</p>
                        </div>
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <section className="border-y border-white/20 py-8" data-testid="faq-lateness-section"><h2 className="font-serif text-lg mb-4">Que se passe-t-il si je suis en retard ?</h2><PublicLatenessPolicy id="faq"/></section>

          {/* Still have a question? */}
          <div className="mt-20 p-10 md:p-14 bg-accent-deep/5 border border-accent-deep/20 rounded text-center">
            <div className="eyebrow mb-4">Toujours une question ?</div>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Écris-moi, <span className="italic-accent">je lis tout.</span></h2>
            <p className="text-text-cream/60 font-light max-w-xl mx-auto mb-8">
              Peu importe la question — timing, cadeau, dress-code, enfant difficile — je réponds personnellement sous 24 h ouvrées.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href="mailto:studioeuclides25@gmail.com" className="btn-primary" data-testid="faq-contact-link">
                <Mail size={14} className="mr-2" /> Me contacter
              </a>
              <Link to="/reservation" className="btn-ghost" data-testid="faq-reservation-link">
                Voir la réservation <ArrowRight size={14} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
